import { ClusterOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { DatePicker, Divider, Select, Spin } from 'antd'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { UserHoverCard } from '@/components'
import {
  CompanyProfileInput,
  UserInfoFragment,
  useCompanyProfileUpdateMutation,
  useGetCompanyProfileQuery,
} from '@/generated/graphql'
import { routes } from '@/routes'
import { Cities } from '@/shares/i18n-mapping'

import { EditButton, ProfileInfo } from '../profile-item.component'
import { ProfileInfoProps } from '../profile.interface'
import s from '../profile.module.less'
import { removeTypeName, setProfilePartialAttribute } from '../profile.util'

interface ProfileBasicPartComponentProps {
  userId: number
  isViewer: boolean
  isEdit: boolean
  onEdit: (isEdit: boolean) => void
}

export const CompanyProfile: React.FC<ProfileBasicPartComponentProps> = ({ userId, isViewer, isEdit, onEdit }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [editedCompanyProfile, setEditedCompanyProfile] = React.useState<CompanyProfileInput>()
  const { data, refetch, networkStatus } = useGetCompanyProfileQuery({
    variables: {
      userId,
    },
    notifyOnNetworkStatusChange: true,
  })
  const [companyProfileUpdate, { loading: updateLoading }] = useCompanyProfileUpdateMutation({
    onCompleted: () => {
      refetch()
      onEdit(false)
    },
    onError: () => {
      onEdit(false)
    },
  })

  React.useEffect(() => {
    if (data) {
      const companyProfile = cloneDeep(data.user.companyProfile)
      removeTypeName(companyProfile)
      setEditedCompanyProfile(companyProfile)
    }
  }, [isEdit])

  const loading = updateLoading || networkStatus === NetworkStatus.refetch
  const companyProfile = data?.user.companyProfile

  const departments = {
    value: data ? data.user.departments?.map((d) => d.name).join(' - ') : '',
    isVisible: true,
  }

  const leaders = {
    value: data ? (data.user.leaders ? data.user.leaders?.map((d) => d.name).join(', ') : '') : '',
    isVisible: true,
  }

  const colleagues = {
    value: data ? (data.user.colleagues ? data.user.colleagues?.map((l) => l.name).join(', ') : '') : '',
    isVisible: true,
  }

  const getProfile = (isEdit: boolean) => (isEdit ? editedCompanyProfile : companyProfile)

  if (!getProfile(isEdit)) {
    return null
  }

  const users = (users: UserInfoFragment[] = []) => (
    <div className={s.customContent}>
      {users.map((user) => (
        <UserHoverCard email={user.email} key={user.userId}>
          <a onClick={() => history.push(routes.users.page(user!.email))}>{user!.name} </a>
        </UserHoverCard>
      ))}
    </div>
  )

  const intros: ProfileInfoProps[] = [
    {
      title: <span>{`${t('profile.company.departmentName')}:`}</span>,
      value: departments,
      attributeChain: 'departmentName',
      readOnly: true,
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.orgLeader')}:`}</span>,
      value: leaders,
      attributeChain: 'orgLeader',
      children: users(data && data.user.leaders),
      readOnly: true,
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.employedDate')}:`}</span>,
      value: getProfile(isEdit)!.employedDate,
      attributeChain: 'employedDate',
      children: isEdit ? (
        <DatePicker
          className={s.datePicker}
          value={moment(getProfile(isEdit)!.employedDate.value ? getProfile(isEdit)!.employedDate.value : moment.now())}
          onChange={(value) =>
            setProfilePartialAttribute(
              getProfile(isEdit)!,
              getProfile(isEdit)!,
              'employedDate.value',
              moment(value).format('YYYY-MM-DD'),
              setEditedCompanyProfile
            )
          }
        />
      ) : (
        <span className={s.readArea}>{getProfile(isEdit)!.employedDate.value}</span>
      ),
      readOnly: true,
    },
    {
      title: <span>{`${t('profile.company.mentor')}:`}</span>,
      value: getProfile(isEdit)!.mentor,
      attributeChain: 'mentor',
    },
    {
      title: <span>{`${t('profile.company.email')}:`}</span>,
      value: getProfile(isEdit)!.email,
      attributeChain: 'email',
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.workPhone')}:`}</span>,
      value: getProfile(isEdit)!.workPhone,
      attributeChain: 'workPhone',
    },
    {
      title: <span>{`${t('profile.company.hrbpContact')}:`}</span>,
      value: { value: '', isVisible: true },
      attributeChain: 'hrbpContact',
      children: data?.user.hrbp && users([data.user.hrbp]),
      readOnly: true,
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.financialContact')}:`}</span>,
      value: { value: '', isVisible: true },
      attributeChain: 'financialContact',
      children: data?.user.financePartner && users([data.user.financePartner]),
      readOnly: true,
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.location')}:`}</span>,
      value: getProfile(isEdit)!.location,
      attributeChain: 'location',
      children: isEdit ? (
        <Select
          disabled={!isEdit}
          bordered={isEdit}
          showArrow={false}
          value={getProfile(isEdit)!.location.value}
          onChange={(value: string) => {
            setProfilePartialAttribute<CompanyProfileInput>(
              getProfile(isEdit)!,
              getProfile(isEdit)!,
              `location.value`,
              value,
              setEditedCompanyProfile
            )
          }}
        >
          {Object.entries(Cities).map(([key, val]) => (
            <Select.Option key={key} value={key}>
              {t(val)}
            </Select.Option>
          ))}
        </Select>
      ) : (
        <span className={s.readArea}>{t(Cities[getProfile(isEdit)!.location.value])}</span>
      ),
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.company.colleagues')}:`}</span>,
      value: colleagues,
      attributeChain: 'colleagues',
      children: users(data && data.user.colleagues),
      readOnly: true,
      disableHidden: true,
    },
  ]

  const header = (
    <div className={s.sectionHeadContainer}>
      <div className={s.sectionHead}>
        <ClusterOutlined className={s.sectionIcon} />
        <span className={s.sectionTitle}>{t('profile.company.companyInfo')}</span>
      </div>
      <div className={s.sectionHeadExtra}>
        <EditButton
          isViewer={isViewer}
          isEdit={isEdit}
          loading={loading}
          onEdit={onEdit}
          onUpdate={async () => {
            await companyProfileUpdate({ variables: { companyProfile: editedCompanyProfile! } })
          }}
        />
      </div>
    </div>
  )

  const content = (
    <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
      {intros.map((intro, index) =>
        isEdit || intro.value.isVisible ? (
          <ProfileInfo
            title={intro.title}
            value={intro.value}
            editMode={isEdit}
            key={index}
            onChange={(value, chain) =>
              setProfilePartialAttribute(
                getProfile(isEdit)!,
                getProfile(isEdit)!,
                chain,
                value,
                setEditedCompanyProfile
              )
            }
            attributeChain={intro.attributeChain}
            link={intro.link}
            readOnly={!!intro.readOnly}
            disableHidden={!!intro.disableHidden}
          >
            {intro.children}
          </ProfileInfo>
        ) : null
      )}
    </div>
  )

  return (
    <div className={s.section}>
      {header}
      <Divider className={s.sectionDivider} />
      {loading ? <Spin className={s.spin} spinning={loading} /> : content}
    </div>
  )
}
